/**
 * LoadingButton
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 *
 * @todo Use template for spinnerHtml?
 * @todo Implement aria-label, aria-disabled, disabled css: https://www.bekk.christmas/post/2023/24/accessible-loading-button
 * @todo Implement success message/icon
 * @todo Implement error message/icon
 * @todo Implement minimum loading time
 */
class LoadingButton extends HTMLElement {
  constructor() {
    super();

    this.initObserver = this.initObserver.bind(this);
    this.setStateIdle = this.setStateIdle.bind(this);
    this.setStateLoading = this.setStateLoading.bind(this);

    this.state = 'idle';
  }

  /**
   * Getters
   */

  /**
   * @type HTMLButtonElement | null
   */
  get button() {
     return this.querySelector('button');
  }

  /**
   * @type number | null
   */
  get buttonWidth() {
    return this.button?.getBoundingClientRect().width;
  }

  /**
   * @type string 
   */
  get spinnerHtml() {
    return `
    <span>
      <svg class="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span class="sr-only">Loading...</span>
    </span>
    `;
  }

  /**
   * connectedCallback
   * Render html/css & attatch event listeners
   */
  connectedCallback() {
    this.originalContent = this.button.innerHTML;
    this.initObserver();
  }

  /**
   * disconnectedCallback
   * Remove listeners.
   */
  disconnectedCallback() {
    this.observer.disconnect();
  }

  initObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes") {
          if (mutation.target.hasAttribute('data-loading') && this.state !== 'loading') {
            this.setStateLoading();
          }
          if (!mutation.target.hasAttribute('data-loading') && this.state === 'loading') {
            this.setStateIdle();
          }
        }
      });
    });

    this.observer.observe(this.button, {
      attributes: true //configure it to listen to attribute changes
    });
  }

  setStateLoading() {
    if (this.buttonWidth) {
      this.button.style.width = `${this.buttonWidth}px`;
    }
    this.button.innerHTML = this.spinnerHtml;
    this.state = 'loading';
  }

  setStateIdle() {
    if (this.buttonWidth) {
      this.button.style.width = '';
    }
    this.button.innerHTML = this.originalContent;
    this.state = 'idle';
  }
}

if (customElements.get("loading-button") === undefined) {
  window.customElements.define("loading-button", LoadingButton);
}
